import React, { useState, useEffect } from 'react';
import { getAnalyticsDashboard, getAllUsers } from '../services/api';
import './AdminDashboard.css';

function AdminDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, usersRes] = await Promise.all([
        getAnalyticsDashboard(),
        getAllUsers()
      ]);

      setAnalytics(analyticsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="container"><div className="loading-spinner">Loading...</div></div>;
  }

  return (
    <div className="container admin-dashboard">
      <div className="page-header">
        <h1>🔧 Admin Dashboard</h1>
        <p>Manage your loyalty program and track performance</p>
      </div>

      <div className="admin-tabs">
        <button 
          className={`admin-tab ${activeView === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveView('overview')}
        >
          Overview
        </button>
        <button 
          className={`admin-tab ${activeView === 'users' ? 'active' : ''}`}
          onClick={() => setActiveView('users')}
        >
          Users
        </button>
        <button 
          className={`admin-tab ${activeView === 'transactions' ? 'active' : ''}`}
          onClick={() => setActiveView('transactions')}
        >
          Recent Transactions
        </button>
      </div>

      {activeView === 'overview' && (
        <>
          {/* Key Metrics */}
          <div className="grid grid-3">
            <div className="admin-stat-card">
              <div className="admin-stat-icon">👥</div>
              <div className="admin-stat-content">
                <h3>Total Members</h3>
                <div className="admin-stat-value">{analytics?.totalMembers || 0}</div>
                <p className="stat-subtext">
                  {analytics?.activeMembers || 0} active this month
                </p>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="admin-stat-icon">💎</div>
              <div className="admin-stat-content">
                <h3>Points Issued</h3>
                <div className="admin-stat-value">{analytics?.totalPointsIssued?.toLocaleString() || 0}</div>
                <p className="stat-subtext">Total points distributed</p>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="admin-stat-icon">🎁</div>
              <div className="admin-stat-content">
                <h3>Redemption Rate</h3>
                <div className="admin-stat-value">{analytics?.redemptionRate || 0}%</div>
                <p className="stat-subtext">
                  {analytics?.totalPointsRedeemed?.toLocaleString() || 0} points redeemed
                </p>
              </div>
            </div>
          </div>

          {/* Members by Tier */}
          <div className="card">
            <h2>Members by Tier</h2>
            <div className="tier-distribution">
              {analytics?.membersByTier && Object.entries(analytics.membersByTier).map(([tier, count]) => (
                <div key={tier} className="tier-bar-container">
                  <div className="tier-bar-label">
                    <span className="tier-name">{tier}</span>
                    <span className="tier-count">{count} members</span>
                  </div>
                  <div className="tier-bar">
                    <div 
                      className="tier-bar-fill"
                      style={{ 
                        width: `${(count / analytics.totalMembers) * 100}%`,
                        background: getTierColor(tier)
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Redeemers */}
          <div className="card">
            <h2>🏆 Top Redeemers</h2>
            {analytics?.topRedeemers && analytics.topRedeemers.length > 0 ? (
              <div className="top-redeemers-list">
                {analytics.topRedeemers.map((redeemer, index) => (
                  <div key={redeemer.userId} className="redeemer-item">
                    <div className="redeemer-rank">#{index + 1}</div>
                    <div className="redeemer-info">
                      <h4>{redeemer.userName}</h4>
                      <p>{redeemer.redemptionCount} redemptions</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-message">No redemptions yet</p>
            )}
          </div>
        </>
      )}

      {activeView === 'users' && (
        <div className="card">
          <h2>All Users</h2>
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Points</th>
                  <th>Lifetime Points</th>
                  <th>Tier</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td className="points-cell">{user.points}</td>
                    <td className="points-cell">{user.lifetimePoints}</td>
                    <td>
                      <span className="tier-badge-small" style={{ background: getTierColor(user.tier) }}>
                        {user.tier}
                      </span>
                    </td>
                    <td>{new Date(user.joinedAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeView === 'transactions' && (
        <div className="card">
          <h2>Recent Transactions</h2>
          {analytics?.recentTransactions && analytics.recentTransactions.length > 0 ? (
            <div className="transactions-table-container">
              <table className="transactions-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Type</th>
                    <th>Points</th>
                    <th>Reason</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.recentTransactions.map(transaction => (
                    <tr key={transaction.id}>
                      <td>{transaction.userName}</td>
                      <td>
                        <span className={`type-badge ${transaction.type}`}>
                          {transaction.type}
                        </span>
                      </td>
                      <td className={`points-cell ${transaction.points > 0 ? 'positive' : 'negative'}`}>
                        {transaction.points > 0 ? '+' : ''}{transaction.points}
                      </td>
                      <td>{transaction.reason}</td>
                      <td>{new Date(transaction.timestamp).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="empty-message">No transactions yet</p>
          )}
        </div>
      )}
    </div>
  );
}

function getTierColor(tier) {
  const colors = {
    bronze: '#CD7F32',
    silver: '#C0C0C0',
    gold: '#FFD700',
    platinum: '#E5E4E2'
  };
  return colors[tier] || '#6b7280';
}

export default AdminDashboard;
